import { useMemo } from 'react'
import type { ExtraPackageContentItem } from 'src/features/ExtraPackage/api/types'
import { useExtraPackages } from 'src/features/ExtraPackage/api/useExtraPackages'
import {
  getCardMeta,
  type PackageContentMeta,
  type PackageMarketingMetadata,
} from 'src/features/ExtraPackage/domain/packCardMeta'
import { useMyInfo } from 'src/services/apollo/auth'
import type { PaymentOpts } from 'src/services/helpers/payment'

/**
 * Pull pack contents + marketing metadata + base credits for the SKU being
 * purchased. Returns empty values for membership purchases (non-credit-pack).
 */
export function useCreditPackData(props: PaymentOpts) {
  const { data: me } = useMyInfo()
  const currentTier = me?.membership?.tier ?? 0

  const isCreditPack = props.itemId?.includes('extra-package') ?? false
  const extraPackages = useExtraPackages({
    currentTier: isCreditPack ? currentTier : undefined,
    promotionOption: isCreditPack ? (props.promotionOption ?? {}) : null,
  })

  return useMemo(() => {
    if (!isCreditPack || !extraPackages) {
      return {
        packageContents: [] as ExtraPackageContentItem[],
        packCardMeta: null,
        baseCredits: undefined as number | undefined,
      }
    }
    // `itemId` may be the bare SKU id (e.g. `extra-package-3`, legacy
    // intentToPay path) or the prefixed form `extra-package:<sku-id>`
    // (used by `usePurchaseExtraPackage`'s oRPC executor). Match either.
    const pack = extraPackages.find(
      p => p.id === props.itemId || `extra-package:${p.id}` === props.itemId,
    )
    const contents = (pack?.packageContents ?? []).filter(item => {
      const md = (item.metadata ?? {}) as PackageContentMeta
      return md.hideInCard !== true
    })
    const tierValue =
      pack?.values.find(v => v.tier === currentTier) ?? pack?.values[0]
    const base = tierValue?.value
    const boosted = tierValue?.creditsAfterBoosts
    return {
      packageContents: contents,
      packCardMeta: getCardMeta(
        pack?.marketingMetadata as PackageMarketingMetadata | null | undefined,
      ),
      baseCredits:
        base != null && boosted != null && boosted > base ? base : undefined,
    }
  }, [isCreditPack, extraPackages, props.itemId, currentTier])
}
